import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tile, Loading, Grid, Column, Tag, Modal, InlineNotification, Pagination, Search, Tooltip, DataTable, Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from '@carbon/react';
import { Download, Receipt, Error, Checkmark, Document, View, Grid as GridIcon, List, Information } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.webp';

const BillingPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, session } = useAuth();
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const successColor = '#24a148';
  const errorColor = '#da1e28';
  const warningColor = '#f1c21b';

  const [payments, setPayments] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
        
        // Fetch payment history from dedicated endpoint
        const paymentsResponse = await fetch('/.netlify/functions/get-payment-history', {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (paymentsResponse.ok) {
          const paymentsData = await paymentsResponse.json();
          // Merge DB payment_history with Stripe paid invoices (source of truth).
          // Stripe paid invoices surface payments the old webhook bug never recorded.
          const dbPayments = paymentsData.payments || [];
          const stripePaid = paymentsData.paidInvoices || [];
          // Dedupe by stripe_invoice_id where present.
          const seenInv = new Set(dbPayments.map((p) => p.stripe_invoice_id).filter(Boolean));
          const mergedPaid = stripePaid.filter((inv) => !seenInv.has(inv.stripe_invoice_id));
          setPayments([...dbPayments, ...mergedPaid]);
          setOpenInvoices(paymentsData.openInvoices || []);
          setUpcomingInvoice(paymentsData.upcomingInvoice || null);
        } else {
          // Fallback to get-subscription endpoint
          const response = await fetch('/.netlify/functions/get-subscription', {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          });

          if (response.ok) {
            const data = await response.json();
            setPayments(data.recentPayments || []);
            setOpenInvoices([]);
            setUpcomingInvoice(null);
          }
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [session?.access_token, user?.access_token]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded': return Checkmark;
      case 'paid': return Checkmark;
      case 'failed': return Error;
      case 'open':
      case 'uncollectible':
        return Error;
      case 'upcoming': return Receipt;
      default: return Receipt;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return successColor;
      case 'paid': return successColor;
      case 'failed': return errorColor;
      case 'open': return warningColor;
      case 'uncollectible': return errorColor;
      case 'upcoming': return '#0f62fe';
      default: return textSecondary;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'AUD'
    }).format(amount);
  };

  const handleDownloadInvoice = (paymentId) => {
    // Use our own PDF generation instead of Stripe
    handleGenerateInvoice(paymentId);
  };

  const handleGenerateInvoice = async (paymentId) => {
    try {
      const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
      const response = await fetch(`/.netlify/functions/generate-invoice?payment_id=${paymentId}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/pdf')) {
          // Handle PDF download
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `invoice-${paymentId}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        } else {
          // Fallback to HTML display
          const htmlContent = await response.text();
          const newWindow = window.open('', '_blank');
          newWindow.document.write(htmlContent);
          newWindow.document.title = `Invoice - ${paymentId}`;
          newWindow.document.close();
        }
      } else {
        const errorData = await response.json();
        alert('❌ Error generating invoice: ' + errorData.error);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('❌ Error generating invoice. Please try again.');
    }
  };

  const handleViewReceipt = (paymentIntentId) => {
    // Open Stripe receipt portal
    window.open(`https://dashboard.stripe.com/payments/${paymentIntentId}`, '_blank');
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const [refundingId, setRefundingId] = useState(null);
  const [refundResult, setRefundResult] = useState(null); // { id, message, kind }
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundModalPayment, setRefundModalPayment] = useState(null);

  // Open the refund confirmation modal for a paid payment/invoice.
  const handleRequestRefund = (payment) => {
    const isPaid = payment.status === 'paid' || payment.status === 'succeeded';
    if (!isPaid) {
      setRefundResult({ id: payment.id, message: 'Only paid invoices can be refunded.', kind: 'error' });
      return;
    }
    setRefundResult(null);
    setRefundModalPayment(payment);
    setRefundModalOpen(true);
  };

  // Execute the refund after the user confirms in the modal.
  const confirmRefund = async () => {
    const payment = refundModalPayment;
    if (!payment) return;

    try {
      setRefundingId(payment.id);
      setRefundResult(null);
      const token = session?.access_token || user?.access_token || (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null);
      const res = await fetch('/.netlify/functions/request-refund', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
        body: JSON.stringify({
          paymentId: payment.id || undefined,
          stripeInvoiceId: payment.stripe_invoice_id || undefined,
          reason: 'requested_by_customer',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setRefundResult({ id: payment.id, message: 'Refund request failed: ' + (data.error || res.statusText), kind: 'error' });
        return;
      }
      const refunded = data.refund?.amount ? formatAmount(data.refund.amount, data.refund.currency) : '';
      setRefundResult({
        id: payment.id,
        message: `Refund${refunded ? ` of ${refunded}` : ''} submitted. Status: ${data.refund?.status || 'processing'}. You'll see it back on your card within 5-10 business days.`,
        kind: 'success',
      });
    } catch (err) {
      console.error('Refund request error:', err);
      setRefundResult({ id: payment.id, message: 'Something went wrong submitting the refund. Please contact support.', kind: 'error' });
    } finally {
      setRefundingId(null);
    }
  };

  // Pagination calculations
  // Filter payments based on search term
  const filteredOpenInvoices = openInvoices.filter((inv) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      inv.description?.toLowerCase().includes(searchLower) ||
      inv.stripe_invoice_id?.toLowerCase().includes(searchLower) ||
      inv.status?.toLowerCase().includes(searchLower) ||
      inv.due_summary?.toLowerCase().includes(searchLower) ||
      inv.amount?.toString().includes(searchLower)
    );
  });

  const filteredPayments = payments.filter(payment => {
    const searchLower = searchTerm.toLowerCase();
    return (
      payment.description?.toLowerCase().includes(searchLower) ||
      payment.payment_type?.toLowerCase().includes(searchLower) ||
      payment.status?.toLowerCase().includes(searchLower) ||
      payment.amount?.toString().includes(searchLower) ||
      payment.currency?.toLowerCase().includes(searchLower)
    );
  }).sort((a, b) => {
    // Newest first. created_at is ISO on both DB rows and Stripe invoices.
    const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
  
  const totalItems = filteredPayments.length;
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPayments = viewMode === 'table' 
    ? filteredPayments.slice(startIndex, endIndex)
    : filteredPayments;

  // DataTable headers/rows for table view
  const tableHeaders = [
    { key: 'description', header: 'Description' },
    { key: 'type', header: 'Type' },
    { key: 'amount', header: 'Amount' },
    { key: 'status', header: 'Status' },
    { key: 'date', header: 'Date' },
  ];

  const tableRows = paginatedPayments.map((payment) => ({
    id: payment.id,
    description: payment.description || `${payment.payment_type} payment`,
    type: payment.payment_type === 'additional_sensors'
      ? 'Additional sensors'
      : payment.payment_type?.replace('_', ' '),
    amount: formatAmount(payment.amount, payment.currency),
    status: payment.status,
    date: formatDate(payment.created_at),
    __raw: payment,
  }));

  // Additional sensors summary (e.g. add-on purchases)
  const additionalSensorPayments = payments.filter(
    (payment) => payment.payment_type === 'additional_sensors'
  );
  const additionalSensorsCount = additionalSensorPayments.length;
  const additionalSensorsTotal = additionalSensorPayments.reduce(
    (sum, payment) => sum + (Number(payment.amount) || 0),
    0
  );
  const additionalSensorsCurrency =
    (additionalSensorPayments[0] && additionalSensorPayments[0].currency) || 'AUD';

  // Reset to page 1 when search changes (align with DeviceListPage)
  useEffect(() => {
    setPage(1);
  }, [searchTerm, viewMode, openInvoices.length]);

  if (loading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} data-testid="billing-page">
        <div style={{ textAlign: 'center', color: textSecondary }}>
          <Loading description="Loading billing history..." withOverlay={false} />
          <p style={{ marginTop: '16px' }}>Loading billing history...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', color: textPrimary }} data-testid="billing-page">
      {/* Header - same structure as DeviceListPage */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                    <h1 style={{ color: textPrimary, fontSize: '1.75rem', fontWeight: 600, margin: 0 }}>Billing History</h1>
                    <Tooltip
                      label="View your complete billing history, download invoices, and track all subscription payments and transactions. Generate PDF invoices for your records and sync historical payments from Stripe."
                      align="bottom"
                      alignOffset={4}
                    >
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: '4px',
                          borderRadius: '50%',
                          cursor: 'pointer',
                          color: textSecondary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        aria-label="Billing help"
                      >
                        <Information size={20} />
                      </button>
                    </Tooltip>
                  </div>
                  <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>
                    {filteredPayments.length} of {payments.length} payments
                    {openInvoices.length > 0 && (
                      <span style={{ marginLeft: '8px' }}>
                        · {filteredOpenInvoices.length} outstanding invoice{filteredOpenInvoices.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </p>
                  {additionalSensorsCount > 0 && (
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: '4px 0 0' }}>
                      Additional sensors: {additionalSensorsCount} payments · {formatAmount(additionalSensorsTotal, additionalSensorsCurrency)}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <Button
                    kind={viewMode === 'cards' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={GridIcon}
                    iconDescription="Card view"
                    onClick={() => setViewMode('cards')}
                  />
                  <Button
                    kind={viewMode === 'table' ? 'secondary' : 'ghost'}
                    size="sm"
                    hasIconOnly
                    renderIcon={List}
                    iconDescription="Table view"
                    onClick={() => setViewMode('table')}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', maxWidth: '400px' }}>
                  <Search
                    labelText="Search"
                    placeholder="Search payments and invoices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="lg"
                    data-testid="search-payments"
                  />
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content - same structure as DeviceListPage */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          {upcomingInvoice && (
            <Column lg={16} md={8} sm={4}>
              <Tile
                style={{
                  marginBottom: '24px',
                  padding: '24px',
                  backgroundColor: isDark ? '#0f1f3d' : '#edf5ff',
                  border: `1px solid #0f62fe`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: 600, color: textPrimary }}>
                      Upcoming invoice
                    </h2>
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.875rem', color: textSecondary }}>
                      {upcomingInvoice.next_payment_attempt
                        ? `Next charge ${formatDate(upcomingInvoice.next_payment_attempt)}`
                        : 'Your next invoice will be generated at the start of the next billing cycle.'}
                    </p>
                    {upcomingInvoice.lines && upcomingInvoice.lines.length > 0 && (
                      <ul style={{ margin: '0', paddingLeft: '18px', color: textSecondary, fontSize: '0.875rem' }}>
                        {upcomingInvoice.lines.map((l, i) => (
                          <li key={i}>{l.description} — {formatAmount(l.amount, upcomingInvoice.currency)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 600, color: textPrimary }}>
                      {formatAmount(upcomingInvoice.amount, upcomingInvoice.currency)}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#0f62fe', fontWeight: 600, textTransform: 'uppercase' }}>
                      Upcoming
                    </span>
                  </div>
                </div>
              </Tile>
            </Column>
          )}
          {filteredOpenInvoices.length > 0 && (
            <Column lg={16} md={8} sm={4}>
              <Tile
                style={{
                  marginBottom: '24px',
                  padding: '24px',
                  backgroundColor: isDark ? '#2d1b00' : '#fff8e1',
                  border: `1px solid ${warningColor}`,
                }}
              >
                <h2 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: 600, color: textPrimary }}>
                  Outstanding invoices
                </h2>
                <p style={{ margin: '0 0 16px 0', fontSize: '0.875rem', color: textSecondary }}>
                  Pay or update your payment method in ChillVerify Customer Portal if a charge did not go through.
                </p>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                    <thead>
                      <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Invoice</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Description</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Amount due</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Due date</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, fontSize: '0.875rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOpenInvoices.map((inv, idx) => (
                        <tr
                          key={inv.id}
                          style={{
                            borderBottom: `1px solid ${borderColor}`,
                            backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                          }}
                        >
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', color: textSecondary, fontFamily: 'monospace' }}>
                            {inv.stripe_invoice_id}
                          </td>
                          <td style={{ padding: '12px 16px', color: textPrimary, fontSize: '0.875rem' }}>{inv.description}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600, color: errorColor }}>
                            {formatAmount(inv.amount, inv.currency)}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '0.875rem', color: textPrimary }}>
                            {inv.due_date ? formatDate(inv.due_date) : '—'}
                            <div style={{ color: textSecondary, fontSize: '0.75rem', marginTop: '4px' }}>{inv.due_summary}</div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {React.createElement(getStatusIcon(inv.status), { size: 16 })}
                              <span style={{ color: getStatusColor(inv.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                                {inv.status}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            {inv.hosted_invoice_url ? (
                              <Button
                                kind="primary"
                                size="sm"
                                onClick={() => window.open(inv.hosted_invoice_url, '_blank', 'noopener,noreferrer')}
                              >
                                Pay online
                              </Button>
                            ) : (
                              <Button kind="secondary" size="sm" onClick={() => navigate('/dashboard/subscription')}>
                                Customer Portal
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tile>
            </Column>
          )}

            {payments.length === 0 && openInvoices.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <Tile style={{ padding: '32px', textAlign: 'center', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                <h3 style={{ marginBottom: '16px', color: textPrimary }}>No billing history</h3>
                <p style={{ color: textSecondary, marginBottom: '24px' }}>
                  You haven't made any payments yet. Subscribe to a plan to see your billing history here.
                </p>
                <Button kind="primary" onClick={() => navigate('/dashboard/subscription')}>
                  View Subscription Plans
                </Button>
              </Tile>
            </Column>
          ) : filteredPayments.length === 0 && filteredOpenInvoices.length === 0 ? (
            <Column lg={16} md={8} sm={4}>
              <Tile style={{ padding: '32px', textAlign: 'center', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                <h3 style={{ marginBottom: '16px', color: textPrimary }}>No results found</h3>
                <p style={{ color: textSecondary, marginBottom: '24px' }}>
                  Nothing matches your search. Try adjusting your search terms.
                </p>
                <Button
                  kind="secondary"
                  onClick={() => {
                    setSearchTerm('');
                    setPage(1);
                  }}
                >
                  Clear Search
                </Button>
              </Tile>
            </Column>
          ) : viewMode === 'cards' ? (
              <>
              {filteredPayments.map((payment) => (
                <Column key={payment.id} lg={8} md={4} sm={4}>
                  <Tile
                    style={{
                      backgroundColor: bgSecondary,
                      border: `1px solid ${borderColor}`,
                      padding: '24px',
                      height: '100%',
                      marginBottom: '16px',
                    }}
                    data-testid={`billing-card-${payment.id}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                      <div>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '1.125rem', fontWeight: '600' }}>
                          {payment.description || `${payment.payment_type} payment`}
                        </h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          <Tag type="blue" size="sm">
                            {(payment.payment_type || 'payment').replace(/_/g, ' ')}
                          </Tag>
                          {payment.payment_type === 'additional_sensors' && (
                            <Tag type="green" size="sm">
                              Additional sensors
                            </Tag>
                          )}
                          {payment.description &&
                            payment.description.toLowerCase().includes('mobile') && (
                              <Tag type="cool-gray" size="sm">
                                Mobile
                              </Tag>
                            )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {React.createElement(getStatusIcon(payment.status), { size: 16 })}
                        <span style={{ color: getStatusColor(payment.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f62fe', marginBottom: '4px' }}>
                        {formatAmount(payment.amount, payment.currency)}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: textSecondary }}>
                        {formatDate(payment.created_at)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={View}
                        onClick={() => handleViewPayment(payment)}
                      >View</Button>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={Document}
                        onClick={() => handleGenerateInvoice(payment.id)}
                      >Invoice</Button>
                      {(payment.status === 'paid' || payment.status === 'succeeded') && (
                        <Button
                          kind="ghost"
                          size="sm"
                          disabled={refundingId === payment.id}
                          onClick={() => handleRequestRefund(payment)}
                        >
                          {refundingId === payment.id ? 'Processing…' : 'Request refund'}
                        </Button>
                      )}
                    </div>
                  </Tile>
                </Column>
              ))}
              {filteredPayments.length === 0 && (
                <Column lg={16} md={8} sm={4}>
                  <Tile style={{ padding: '24px', textAlign: 'center', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                    <p style={{ color: textSecondary, margin: 0 }}>
                      No payment records match your search. Outstanding invoices are listed above when applicable.
                    </p>
                  </Tile>
                </Column>
              )}
              </>
            ) : (
              // Table View
              <Column lg={16} md={8} sm={4}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: bgSecondary, border: `1px solid ${borderColor}` }}>
                    <thead>
                      <tr style={{ backgroundColor: isDark ? '#353535' : '#e0e0e0' }}>
                        <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Date</th>
                        <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Description</th>
                        <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Amount</th>
                        <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Status</th>
                        <th style={{ padding: '16px', textAlign: 'left', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Type</th>
                        <th style={{ padding: '16px', textAlign: 'right', color: textPrimary, fontWeight: 600, fontSize: '0.875rem', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPayments.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: textSecondary }}>
                            No payment records match your search.
                          </td>
                        </tr>
                      ) : (
                      paginatedPayments.map((payment, idx) => (
                        <tr
                          key={payment.id}
                          style={{
                            borderBottom: `1px solid ${borderColor}`,
                            backgroundColor: idx % 2 === 0 ? bgSecondary : bgPrimary,
                          }}
                        >
                          <td style={{ padding: '16px', color: textSecondary, fontSize: '0.875rem' }}>
                            {formatDate(payment.created_at)}
                          </td>
                          <td style={{ padding: '16px' }}>
                            <div>
                              <p style={{ color: textPrimary, fontWeight: 500, marginBottom: '2px' }}>
                                {payment.description || `${payment.payment_type} payment`}
                              </p>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                <Tag type="blue" size="sm">
                                  {(payment.payment_type || 'payment').replace(/_/g, ' ')}
                                </Tag>
                                {payment.payment_type === 'additional_sensors' && (
                                  <Tag type="green" size="sm">
                                    Additional sensors
                                  </Tag>
                                )}
                                {payment.description &&
                                  payment.description.toLowerCase().includes('mobile') && (
                                    <Tag type="cool-gray" size="sm">
                                      Mobile
                                    </Tag>
                                  )}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px' }}>
                            <div style={{ color: '#0f62fe', fontWeight: 600 }}>
                              {formatAmount(payment.amount, payment.currency)}
                            </div>
                          </td>
                          <td style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {React.createElement(getStatusIcon(payment.status), { size: 16 })}
                              <span style={{ color: getStatusColor(payment.status), fontSize: '0.875rem', textTransform: 'capitalize' }}>
                                {payment.status}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '16px' }}>
                            <span style={{ 
                              textTransform: 'capitalize',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              backgroundColor: isDark ? '#262626' : '#f4f4f4',
                              fontSize: '0.875rem',
                              color: textSecondary
                            }}>
                              {(payment.payment_type || 'payment').replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                              <Button 
                                kind="ghost" 
                                size="sm" 
                                hasIconOnly 
                                renderIcon={View} 
                                iconDescription="View" 
                                onClick={() => handleViewPayment(payment)} 
                              />
                              <Button 
                                kind="ghost" 
                                size="sm" 
                                hasIconOnly 
                                renderIcon={Document} 
                                iconDescription="Generate Invoice" 
                                onClick={() => handleDownloadInvoice(payment.id)} 
                              />
                              {payment.stripe_payment_intent_id && (
                                <Button 
                                  kind="ghost" 
                                  size="sm" 
                                  hasIconOnly 
                                  renderIcon={Receipt} 
                                  iconDescription="View Receipt" 
                                  onClick={() => handleViewReceipt(payment.stripe_payment_intent_id)} 
                                />
                              )}
                              {(payment.status === 'paid' || payment.status === 'succeeded') && (
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  disabled={refundingId === payment.id}
                                  iconDescription="Request refund"
                                  onClick={() => handleRequestRefund(payment)}
                                >
                                  {refundingId === payment.id ? '…' : 'Refund'}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination - only show for table view */}
                {totalItems > 0 && (
                  <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      backwardText="Previous"
                      forwardText="Next"
                      itemsPerPageText="Items per page:"
                      page={page}
                      pageNumberText="Page Number"
                      pageSize={pageSize}
                      pageSizes={[10, 20, 30, 40, 50]}
                      size="md"
                      totalItems={totalItems}
                      onChange={({ page, pageSize }) => {
                        setPage(page);
                        setPageSize(pageSize);
                      }}
                    />
                  </div>
                )}
              </Column>
            )
          }
        </Grid>
      </div>

      {/* Payment Details Modal */}
      <Modal
        open={modalOpen}
        onRequestClose={() => setModalOpen(false)}
        modalHeading="Payment Details"
        modalLabel="Invoice"
        primaryButtonText="Generate Invoice"
        secondaryButtonText="Close"
        onRequestSubmit={() => {
          if (selectedPayment) {
            handleGenerateInvoice(selectedPayment.id);
            setModalOpen(false);
          }
        }}
        size="md"
      >
        {selectedPayment && (
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
              <img
                src={logo}
                alt="ChillVerify"
                style={{ height: '40px' }}
              />
              <div>
                <h3 style={{ margin: '0', fontSize: '1.25rem', fontWeight: '600', color: textPrimary }}>ChillVerify</h3>
                <p style={{ margin: '0', color: textSecondary, fontSize: '0.875rem' }}>Cold Chain Monitoring Solutions</p>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', color: textSecondary, marginBottom: '8px' }}>Payment Information</h4>
                <div style={{ fontSize: '0.875rem', color: textPrimary }}>
                  <p style={{ margin: '4px 0' }}><strong>Date:</strong> {formatDate(selectedPayment.created_at)}</p>
                  <p style={{ margin: '4px 0' }}><strong>Amount:</strong> {formatAmount(selectedPayment.amount, selectedPayment.currency)}</p>
                  <p style={{ margin: '4px 0' }}><strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedPayment.status}</span></p>
                  <p style={{ margin: '4px 0' }}><strong>Type:</strong> {(selectedPayment.payment_type || 'payment').replace(/_/g, ' ')}</p>
                </div>
              </div>
              <div>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', color: textSecondary, marginBottom: '8px' }}>Transaction Details</h4>
                <div style={{ fontSize: '0.875rem', color: textPrimary }}>
                  <p style={{ margin: '4px 0' }}><strong>ID:</strong> {selectedPayment.id}</p>
                  {selectedPayment.stripe_payment_intent_id && (
                    <p style={{ margin: '4px 0' }}><strong>Payment Intent:</strong> {selectedPayment.stripe_payment_intent_id}</p>
                  )}
                  {(() => {
                    const hasStripeInvoice = !!selectedPayment.stripe_invoice_id;
                    const isAdditionalSensors = selectedPayment.payment_type === 'additional_sensors';
                    const appInvoiceLabel = isAdditionalSensors && !hasStripeInvoice
                      ? `App invoice ${selectedPayment.id.substring(0, 8).toUpperCase()}`
                      : null;
                    const invoiceLabel = hasStripeInvoice ? selectedPayment.stripe_invoice_id : appInvoiceLabel;
                    if (!invoiceLabel) return null;
                    return (
                      <p style={{ margin: '4px 0' }}>
                        <strong>Invoice:</strong> {invoiceLabel}
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
            <div>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', textTransform: 'uppercase', color: textSecondary, marginBottom: '8px' }}>Description</h4>
              <p style={{ margin: '0', fontSize: '0.875rem', color: textPrimary }}>
                {selectedPayment.description || `${selectedPayment.payment_type} payment`}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Refund Confirmation Modal */}
      <Modal
        open={refundModalOpen}
        danger
        modalHeading="Request Refund"
        modalLabel="Confirm Refund"
        primaryButtonText={refundingId ? 'Processing…' : 'Request Refund'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={!!refundingId}
        onRequestClose={() => {
          if (!refundingId) {
            setRefundModalOpen(false);
            setRefundModalPayment(null);
            setRefundResult(null);
          }
        }}
        onSecondarySubmit={() => {
          if (!refundingId) {
            setRefundModalOpen(false);
            setRefundModalPayment(null);
            setRefundResult(null);
          }
        }}
        onRequestSubmit={() => {
          if (!refundingId && !refundResult) {
            confirmRefund();
          } else if (refundResult?.kind === 'success') {
            setRefundModalOpen(false);
            setRefundModalPayment(null);
            setRefundResult(null);
          }
        }}
        size="sm"
      >
        {refundModalPayment && (
          <div>
            {refundResult ? (
              <InlineNotification
                kind={refundResult.kind}
                title={refundResult.kind === 'error' ? 'Refund failed' : 'Refund submitted'}
                subtitle={refundResult.message}
                lowContrast
                hideCloseButton
                style={{ marginBottom: '16px' }}
              />
            ) : (
              <>
                <p style={{ margin: '0 0 16px 0', fontWeight: '500', color: textPrimary }}>
                  Are you sure you want to request a refund of {formatAmount(refundModalPayment.amount, refundModalPayment.currency)}?
                </p>
                <p style={{ margin: '0 0 16px 0', color: textSecondary, lineHeight: '1.6' }}>
                  {refundModalPayment.description || (refundModalPayment.payment_type || 'payment').replace(/_/g, ' ')}
                </p>
                <div style={{
                  padding: '12px',
                  backgroundColor: isDark ? '#2d1b1b' : '#fef2f2',
                  border: `1px solid ${isDark ? '#5c1a1a' : '#fecaca'}`,
                  borderRadius: '6px',
                }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: isDark ? '#fca5a5' : '#dc2626' }}>
                    <strong>Important:</strong> Refunds are processed per Stripe's policy and may take 5–10 business days to appear on your card. This action cannot be undone.
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Help Section - below content, same spacing as DeviceListPage content area */}
      <div style={{ padding: '24px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ padding: '16px', backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, borderRadius: '8px' }}>
              <h4 style={{ marginBottom: '12px', color: textPrimary }}>Need help with billing?</h4>
              <p style={{ color: textSecondary, marginBottom: '16px' }}>
                If you have questions about your charges or need a refund, please contact our support team.
              </p>
              <Button kind="secondary" size="sm" onClick={() => navigate('/support')}>
                Contact Support
              </Button>
            </div>
          </Column>
        </Grid>
      </div>
    </div>
  );
};

export default BillingPage;
